<template>
  <div class="map-wrap">
    <div ref="mapEl" class="map"></div>

    <div class="tb-mock">⚡ GPS simulado (LocalStorage)</div>

    <button class="tb-follow" :class="{ off: !sim.follow }" @click="sim.follow = !sim.follow">
      <ion-icon :icon="sim.follow ? navigate : navigateOutline" />
      {{ sim.follow ? 'Siguiendo' : 'Manual' }}
    </button>

    <div class="tb-status">
      <span class="dot"></span>
      {{ sim.selectedRoute.nombre }} • {{ sim.selectedRoute.buses.length }} bus(es) activo(s) • hace {{ ultAct }}s
    </div>

    <div ref="popupEl" class="tb-popup" v-show="popup.show">
      <span class="pp-ruta">{{ popup.ruta }}</span><b>{{ popup.placa }}</b><br>
      Velocidad: {{ popup.vel }} km/h<br>
      <small>Actualizado hace {{ ultAct }}s</small>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { IonIcon } from '@ionic/vue';
import { navigate, navigateOutline } from 'ionicons/icons';

import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import XYZ from 'ol/source/XYZ';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import CircleStyle from 'ol/style/Circle';
import Text from 'ol/style/Text';
import Overlay from 'ol/Overlay';
import { fromLonLat } from 'ol/proj';
import { boundingExtent } from 'ol/extent';

import { sim, startSim } from '../stores/simulation';
import { pointAt } from '../data/mock';

const props = defineProps({ active: { type: Boolean, default: false } });

const mapEl = ref(null);
const popupEl = ref(null);
const popup = reactive({ show: false, placa: '', ruta: '', vel: 0 });
const ultAct = ref(0);

let map = null, overlay = null, inited = false;
let routeSrc, stopSrc, busSrc;

/* ---------- estilos ---------- */
function busStyle(brg) {
  return new Style({
    image: new CircleStyle({
      radius: 15, fill: new Fill({ color: '#1A237E' }),
      stroke: new Stroke({ color: '#fff', width: 3 })
    }),
    text: new Text({
      text: '\u{1F68C}', font: '15px sans-serif',
      rotation: brg + Math.PI / 2, offsetY: -1
    })
  });
}

/* ---------- capas ---------- */
function rebuildRoutes() {
  routeSrc.clear();
  sim.routes.forEach(r => {
    const f = new Feature({ geometry: new LineString(r.path.map(p => fromLonLat(p))) });
    const sel = r.id === sim.selected;
    f.setStyle(new Style({
      stroke: new Stroke({ color: r.color, width: sel ? 6 : 4, opacity: sel ? 0.95 : 0.30 })
    }));
    routeSrc.addFeature(f);
  });
}

function rebuildStops() {
  stopSrc.clear();
  const r = sim.selectedRoute;
  r.stopNums.forEach((n, i) => {
    const p = pointAt(r.path, r.cum, r.cumTot * ((i + 0.5) / r.stopNums.length));
    const f = new Feature({ geometry: new Point(fromLonLat(p.coord)) });
    f.setStyle(new Style({
      image: new CircleStyle({
        radius: 11, fill: new Fill({ color: '#ffffff' }),
        stroke: new Stroke({ color: r.color, width: 3 })
      }),
      text: new Text({
        text: String(n), font: 'bold 10px Inter, sans-serif',
        fill: new Fill({ color: '#141A5E' })
      })
    }));
    stopSrc.addFeature(f);
  });
}

function rebuildBuses() {
  busSrc.clear();
  sim.routes.forEach(r => r.buses.forEach(b => {
    const p = pointAt(r.path, r.cum, b.dist);
    const f = new Feature({ geometry: new Point(fromLonLat(p.coord)) });
    f.set('bus', true); f.set('placa', b.placa); f.set('ruta', r.id); f.set('vel', b.vel || b.speed);
    f.setStyle(busStyle(p.brg));
    b._feat = f;
    busSrc.addFeature(f);
  }));
}

function fitRoute(r) {
  map.getView().fit(boundingExtent(r.path.map(p => fromLonLat(p))),
    { padding: [70, 70, 70, 70], duration: 500, maxZoom: 16 });
}

function onMapClick(e) {
  const f = map.forEachFeatureAtPixel(e.pixel, ft => ft);
  if (f && f.get('bus')) {
    popup.show = true; popup.placa = f.get('placa');
    popup.ruta = f.get('ruta'); popup.vel = f.get('vel');
    overlay.setPosition(f.getGeometry().getCoordinates());
  } else {
    popup.show = false;
  }
}

/* ---------- init perezoso del mapa ---------- */
function init() {
  if (inited) return;
  inited = true;

  routeSrc = new VectorSource();
  stopSrc = new VectorSource();
  busSrc = new VectorSource();

  map = new Map({
    target: mapEl.value,
    layers: [
      // CARTO Voyager (datos OSM, sin bloqueos de tile policy)
      new TileLayer({
        source: new XYZ({
          url: 'https://{a-d}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
          attributions: '© OpenStreetMap contributors © CARTO'
        })
      }),
      new VectorLayer({ source: routeSrc, zIndex: 2 }),
      new VectorLayer({ source: stopSrc, zIndex: 3 }),
      new VectorLayer({ source: busSrc, zIndex: 4 })
    ],
    view: new View({ center: fromLonLat([-90.5132, 14.6442]), zoom: 15.3, maxZoom: 19 })
  });

  overlay = new Overlay({
    element: popupEl.value,
    positioning: 'bottom-center', offset: [0, -18], stopEvent: false
  });
  map.addOverlay(overlay);
  map.on('click', onMapClick);

  rebuildRoutes();
  rebuildStops();
  rebuildBuses();
  nextTick(() => map.updateSize());
}

/* ---------- watchers ---------- */
watch(() => props.active, (a) => {
  if (a) { init(); map.updateSize(); }
});

watch(() => sim.selected, () => {
  if (!inited) return;
  rebuildRoutes();
  rebuildStops();
  fitRoute(sim.selectedRoute);
});

// Cada tick de la simulación: mover buses y (si aplica) seguir
watch(() => sim.lastTick, () => {
  if (!inited) return;
  sim.routes.forEach(r => r.buses.forEach(b => {
    if (!b._feat) return;
    const p = pointAt(r.path, r.cum, b.dist);
    b._feat.getGeometry().setCoordinates(fromLonLat(p.coord));
    b._feat.setStyle(busStyle(p.brg));
    b._feat.set('vel', b.vel);
  }));
  if (sim.follow) {
    const r = sim.selectedRoute;
    const p = pointAt(r.path, r.cum, r.buses[0].dist);
    map.getView().animate({ center: fromLonLat(p.coord), duration: 700 });
  }
});

/* ---------- ciclo de vida ---------- */
let tU = null;
onMounted(() => {
  startSim();
  if (props.active) init();
  tU = setInterval(() => {
    ultAct.value = Math.floor((Date.now() - sim.lastTick) / 1000);
  }, 1000);
});
onBeforeUnmount(() => clearInterval(tU));
</script>

<style scoped>
.map-wrap {
  position: relative; flex: 1 1 auto; min-height: 320px;
  margin: 10px 12px 14px; border-radius: 18px; overflow: hidden;
  box-shadow: 0 6px 20px rgba(20, 26, 94, .18);
}
.map { width: 100%; height: 100%; background: #DDE3EE; }

.tb-status {
  position: absolute; left: 50%; transform: translateX(-50%); bottom: 12px;
  background: #fff; border-radius: 999px; padding: 8px 16px; font-size: 12.5px;
  color: var(--tb-ink); box-shadow: 0 4px 14px rgba(20, 26, 94, .20);
  display: flex; align-items: center; gap: 8px; white-space: nowrap; z-index: 4;
}
.tb-status .dot { width: 9px; height: 9px; border-radius: 50%; background: var(--tb-green); }

.tb-follow {
  position: absolute; top: 12px; right: 12px; border: none; cursor: pointer;
  border-radius: 999px; padding: 10px 18px; font-size: 13px; font-weight: 600; color: #fff;
  background: var(--tb-green); box-shadow: 0 4px 12px rgba(67, 160, 71, .4);
  display: flex; align-items: center; gap: 6px; z-index: 4; font-family: inherit; transition: background .2s;
}
.tb-follow.off { background: var(--tb-blue); box-shadow: 0 4px 12px rgba(33, 150, 243, .4); }

.tb-mock {
  position: absolute; top: 12px; left: 12px; background: rgba(20, 26, 94, .85);
  color: #fff; border-radius: 999px; padding: 5px 12px; font-size: 11px; z-index: 4; letter-spacing: .3px;
}

.tb-popup {
  background: #fff; border-radius: 14px; padding: 10px 14px;
  box-shadow: 0 8px 24px rgba(20, 26, 94, .30); font-size: 12.5px; color: var(--tb-ink);
  border: 2px solid var(--tb-navy2); min-width: 170px;
}
.tb-popup b { color: var(--tb-navy2); font-size: 13.5px; }
.tb-popup .pp-ruta {
  display: inline-block; background: var(--tb-navy2); color: #fff;
  border-radius: 6px; padding: 1px 8px; font-weight: 700; margin-right: 6px;
}
.tb-popup small { color: var(--tb-muted); }
</style>
